import styled, { css } from "styled-components"
import { futuristicListMarker } from "@components/base/listMarkers"

const Section = styled.section`
  position: relative;
  width: 100%;
  padding: clamp(5rem, 10vw, 8.5rem) 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background:
    radial-gradient(circle at 0% 0%, rgba(99, 195, 255, 0.08), transparent 55%), linear-gradient(165deg, #050d1f 0%, #081932 45%, #0f273c 100%);
  color: #f5f9ff;

  &::before {
    content: "";
    position: absolute;
    inset: 10% 32% auto 32%;
    height: 68%;
    background: radial-gradient(circle, rgba(94, 244, 255, 0.12), transparent 65%);
    filter: blur(80px);
    opacity: 0.85;
    pointer-events: none;
  }
`

const Content = styled.div`
  position: relative;
  z-index: 1;
  width: min(1200px, 92vw);
  display: grid;
  gap: clamp(3rem, 6vw, 4.5rem);
`

const Header = styled.header`
  display: grid;
  gap: 1.25rem;
  max-width: 760px;

  @media (max-width: 960px) {
    justify-self: center;
    text-align: center;
  }
`

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.45rem 1.45rem 0.45rem 1.2rem;
  border-radius: 2px;
  background: rgba(98, 198, 255, 0.12);
  color: #63c3ff;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.25em;
  text-transform: uppercase;

  &::before {
    content: "";
    width: 1.1rem;
    height: 0.7rem;
    clip-path: polygon(15% 0%, 85% 0%, 100% 50%, 85% 100%, 15% 100%, 0% 50%);
    background: linear-gradient(135deg, #63ffca 0%, #63c3ff 60%, #6a7bff 100%);
    box-shadow: 0 12px 20px rgba(30, 94, 152, 0.35);
  }
`

const Title = styled.h2`
  margin: 0;
  font-size: clamp(2.4rem, 5.4vw, 3.3rem);
  line-height: 1.08;
  /* font-weight: 700; */
`

const Description = styled.p`
  margin: 0;
  color: rgba(245, 249, 255, 0.82);
  font-size: 1.08rem;
  line-height: 1.75;
`

const ServiceRows = styled.div`
  display: grid;
  gap: clamp(3rem, 6vw, 5rem);
`

const ServiceRow = styled.article<{ $align: "left" | "right"; $isVisible: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: clamp(2rem, 5vw, 4rem);
  align-items: center;
  padding: clamp(2.4rem, 5vw, 3.4rem);
  border-radius: 2px;
  background: rgba(9, 22, 38, 0.78);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 40px 120px rgba(5, 13, 31, 0.55);
  overflow: hidden;
  backdrop-filter: blur(18px);
  scroll-margin-top: 120px;
  transition:
    opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.7s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.4s ease;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transform: translate3d(0, ${({ $isVisible }) => ($isVisible ? "0" : "70px")}, 0);
  will-change: opacity, transform;
  transition-delay: ${({ $align, $isVisible }) => ($isVisible ? ($align === "left" ? "80ms" : "160ms") : "0ms")};

  &:hover {
    transform: translate3d(0, -8px, 0);
    box-shadow: 0 50px 140px rgba(8, 21, 38, 0.65);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none;
    opacity: 1;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    text-align: center;
    padding: clamp(2rem, 6vw, 2.6rem);
  }
`

const ServiceCopy = styled.div<{ $align: "left" | "right" }>`
  display: grid;
  gap: 1.25rem;
  justify-items: ${({ $align }) => ($align === "left" ? "start" : "end")};
  text-align: ${({ $align }) => ($align === "left" ? "left" : "right")};

  @media (max-width: 960px) {
    justify-items: center;
    text-align: center;
  }
`

const StepTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.4rem 1.3rem 0.4rem 1rem;
  border-radius: 2px;
  background: rgba(99, 255, 202, 0.12);
  color: #63ffca;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;

  &::before {
    content: "";
    width: 1rem;
    height: 0.65rem;
    background: linear-gradient(135deg, #63ffca 0%, #63c3ff 70%, #6a7bff 100%);
    clip-path: polygon(15% 0%, 85% 0%, 100% 50%, 85% 100%, 15% 100%, 0% 50%);
    box-shadow: 0 10px 20px rgba(18, 82, 65, 0.25);
  }
`

const ServiceTitle = styled.h3`
  margin: 0;
  font-size: clamp(1.9rem, 4vw, 2.35rem);
  line-height: 1.12;
  font-weight: 500;
`

const ServiceDescription = styled.p`
  margin: 0;
  color: rgba(245, 249, 255, 0.85);
  font-size: 1.05rem;
  line-height: 1.7;
  max-width: 420px;

  @media (max-width: 960px) {
    max-width: 480px;
  }
`

const Highlights = styled.ul<{ $align: "left" | "right" }>`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.85rem;
  font-size: 0.98rem;
  color: rgba(245, 249, 255, 0.88);
  max-width: 420px;

  li {
    ${futuristicListMarker};
  }

  /* For right-hand cards, keep bullets exactly the same but:
     - align the whole list block to the right column
     - keep inner text left so bullets and labels share a clean left edge */
  ${({ $align }) =>
    $align === "right" &&
    css`
      justify-self: flex-end;

      li {
        text-align: left;
      }
    `}

  @media (max-width: 960px) {
    justify-self: center;

    li {
      text-align: left;
    }
  }
`

const ServiceVisual = styled.div<{ $image: string; $align: "left" | "right" }>`
  position: relative;
  width: 100%;
  aspect-ratio: 5 / 4;
  border-radius: 2px;
  overflow: hidden;
  background: radial-gradient(circle at 30% 20%, rgba(99, 195, 255, 0.18), transparent 70%);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: url(${({ $image }) => JSON.stringify($image)}) center/cover;
    filter: saturate(1.15) brightness(0.88);
    transition:
      transform 0.6s ease,
      filter 0.6s ease;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(${({ $align }) => ($align === "left" ? "135deg" : "225deg")}, rgba(5, 13, 31, 0.1), rgba(5, 13, 31, 0.65));
    mix-blend-mode: lighten;
  }

  ${ServiceRow}:hover &::before {
    transform: scale(1.08);
    filter: saturate(1.2) brightness(1);
  }
`

const AccentOrbit = styled.div`
  position: absolute;
  inset: -30%;
  border-radius: 50%;
  border: 1px solid rgba(99, 195, 255, 0.12);
  pointer-events: none;
  transform: rotate(8deg);
`

const Timeline = styled.div`
  position: absolute;
  top: 12%;
  bottom: 12%;
  left: 50%;
  width: 1px;
  transform: translateX(-50%);
  background: linear-gradient(180deg, transparent 0%, rgba(99, 195, 255, 0.35) 25%, rgba(99, 255, 202, 0.5) 55%, transparent 100%);
  opacity: 0.6;

  @media (max-width: 960px) {
    display: none;
  }
`

const services = [
  {
    title: "Communication et accessibilité",
    description: "Nous recevons les demandes, établissons leur priorité et vous tenons au courant des interventions qui nécessitent une décision.",
    highlights: ["Portail de suivi des demandes et des interventions", "Ligne d’urgence avec protocole d’escalade"],
    tag: "Disponibilité",
    image: "/img/cta-management.jpg",
    anchorId: "gestion-247",
  },
  {
    title: "Gestion locative et administration",
    description: "Nous prenons en charge la perception des loyers, les baux, les renouvellements et le suivi administratif prévu au mandat.",
    highlights: ["Suivi des revenus et dépenses selon la formule retenue", "Baux, relevés 31 et renouvellements suivis au calendrier"],
    tag: "Administration",
    image: "/img/about-hero.jpg",
  },
  {
    title: "Trouver de nouveaux locataires",
    description: "Nous préparons l’annonce, coordonnons les visites et documentons les vérifications prévues avant la sélection d’un candidat.",
    highlights: ["Diffusion des annonces et coordination des visites", "Vérifications réalisées avec le consentement du candidat"],
    tag: "Mise en location",
    image: "/img/modern-apartment.jpg",
    anchorId: "selection-locataires",
  },
  {
    title: "Entretien et améliorations",
    description: "Nous planifions l’entretien, demandons les autorisations nécessaires et coordonnons les fournisseurs selon vos paramètres.",
    highlights: ["Suivi des travaux et compte rendu des interventions", "Estimations présentées pour approbation avant les travaux non urgents"],
    tag: "Entretien",
    image: "/img/tempbg.jpg",
  },
]

const ServicesSection = () => {
  return (
    <Section>
      <AccentOrbit />
      <Timeline />
      <Content>
        <Header>
          <Eyebrow>Services propriétaires</Eyebrow>
          <Title>Une gestion structurée, du quotidien aux travaux.</Title>
          <Description>
            Le mandat est défini avec vous. Nous coordonnons ensuite les tâches retenues, du premier appel d’un locataire au suivi d’un fournisseur.
          </Description>
        </Header>
        <ServiceRows>
          {services.map((service, index) => {
            const align: "left" | "right" = index % 2 === 0 ? "left" : "right"
            const isVisible = true

            return (
              <ServiceRow key={service.title} id={service.anchorId} $align={align} $isVisible={isVisible}>
                {align === "left" ? (
                  <>
                    <ServiceCopy $align={align}>
                      <StepTag>{service.tag}</StepTag>
                      <ServiceTitle>{service.title}</ServiceTitle>
                      <ServiceDescription>{service.description}</ServiceDescription>
                      <Highlights $align={align}>
                        {service.highlights.map((highlight) => (
                          <li key={highlight}>{highlight}</li>
                        ))}
                      </Highlights>
                    </ServiceCopy>
                    <ServiceVisual $image={service.image} $align={align} />
                  </>
                ) : (
                  <>
                    <ServiceVisual $image={service.image} $align={align} />
                    <ServiceCopy $align={align}>
                      <StepTag>{service.tag}</StepTag>
                      <ServiceTitle>{service.title}</ServiceTitle>
                      <ServiceDescription>{service.description}</ServiceDescription>
                      <Highlights $align={align}>
                        {service.highlights.map((highlight) => (
                          <li key={highlight}>{highlight}</li>
                        ))}
                      </Highlights>
                    </ServiceCopy>
                  </>
                )}
              </ServiceRow>
            )
          })}
        </ServiceRows>
      </Content>
    </Section>
  )
}

export default ServicesSection
